import React, { useMemo, useEffect, useCallback, useState } from 'react'
import ProtectedImage from 'shared/components/ProtectedImage'
import SelectAutocomplete from 'shared/components/SelectAutocomplete'
import useQuery from 'shared/hooks/useQuery'
import useMutation, { useUpdateNode } from 'shared/hooks/useMutation'
import useSearch from 'shared/hooks/useSearch'
import flowRight from 'lodash/flowRight'
import pick from 'lodash/pick'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import queryString from 'qs'
import UserPlaceholder from 'assets/images/user_placeholder.png'
import { participatFields } from 'pages/CheckIn/components/Dialogs/SelectCheckInOut'
import { ParticipantRowItem } from 'pages/Participants/model/participant'
import { Option } from 'react-select/src/filters'
import Button from 'shared/components/Button'
import SVGScreenedIcon from 'assets/icons/Screened'
import IconRemove from 'assets/icons/remove.svg'
import IconAddParticipant from 'assets/icons/add-user.svg'
import dayjs from 'dayjs'
import day from 'dayjs'
import Box from 'shared/components/Box'
import Tabs from 'shared/components/Tabs'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { showDialog, hideDialog, dialogProcessing, setRefreshPage, showSuccess } from 'shared/redux/app/reducer'
import CheckInDetailsDialog from './CheckInDetails'
import { PAGENAME } from 'pages/CheckIn/model/checkin'
import validator, { getEmptyCheckinFields } from 'pages/CheckIn/model/checkinField'
import { roleSelector, organizationSettingSelector } from 'shared/hocs/withAuth'
import { useSelector } from 'react-redux'
import CheckOutDetailsDialog from 'pages/CheckIn/components/Dialogs/CheckOutDetails'
import checkOutValidator, { CheckOutFieldsType, getEmptyCheckoutFields } from 'pages/CheckIn/model/checkoutField'
/*
	onScan Docu: https://github.com/axenox/onscan.js
*/
const onScan: any = require('onscan.js')

export type CheckInForm = {
  selected: ParticipantRowItem[]
}

type ExtraProp = {
  tabContentClassName?: string
  checkType: string
  refetch: any
  createState: any
}

type CheckInParticipantProps = WrappedProps<CheckInForm> & ExtraProp
function CheckInParticipant(props: CheckInParticipantProps) {
  const dispatch = useDispatch()
  const { isKiosk } = useSelector(roleSelector)
  const { organization_information } = useSelector(organizationSettingSelector)
  const [auto, setAuto] = useState(isKiosk ? true : false)
  const [page, setPage] = useState(0)
  const [participantListCount, setParticipantListCount] = useState(0)
  const [participantList, setParticipantList] = useState<ParticipantRowItem[]>([])
  const [pageSize, setPageSize] = useState(10)
  const [memberId, setMemberId] = useState('')
  const {
    formState,
    formHandlers,
    checkType,
    refetch,
    onCancel,
    onContinue,
    createState,
    tabContentClassName = '',
  } = props
  const { fields } = formState
  const [search, onSearch] = useSearch()
  const filter = useMemo(() => {
    return {
      page: page,
      size: pageSize,
      statuses: ['Active', 'Alumni', 'Inactive'],
      search: {
        fields: ['first_name', 'last_name', 'member_id', 'full_name'],
        value: search,
      },
    }
  }, [search, page, pageSize])

  const dateFilter = {
    start: dayjs()
      .startOf('day')
      .toISOString(),
    end: dayjs()
      .endOf('day')
      .toISOString(),
  }

  const requestURL = useMemo(() => {
    return checkType !== 'out'
      ? `/participant_report?${queryString.stringify({ ...filter })}`
      : `/check-in?${queryString.stringify({ ...filter, ...dateFilter, type: 'notYetCheckout' })}`
  }, [filter])

  const [queryState, queryStateHandler] = useQuery<any>(
    { url: requestURL },
    { initialData: [], isBase: checkType !== 'out', skip: !search },
  )

  // const [, onUpdate] = useUpdateNode({ node: 'check-in', method: 'PUT' })

  const [, onFetchScreeningusers] = useMutation({
    method: 'POST',
    url: `/note/user/daily/screening`,
    isBase: false,
  })
  const { loading } = queryState

  const onScrollBottom = useCallback(
    (e: any) => {
      if (participantListCount > participantList.length) {
        setPage(prevState => {
          return prevState + 1
        })
      }
    },
    [queryStateHandler, participantListCount, participantList, setPage],
  )

  useEffect(() => {
    if (search.trim().length > 0) {
      setPage(0)
      setParticipantList([])
      setParticipantListCount(0)
    }
  }, [search, setPage, setParticipantList, setParticipantListCount])

  useEffect(() => {
    if (queryState.data.data) {
      setParticipantList(prevState => {
        return prevState.concat(queryState.data.data)
      })
      setParticipantListCount(queryState.data.count)
    }
  }, [queryState.data])

  const selectedId = fields.selected.map(e => e.id)
  const filteredList = useMemo(() => {
    let list: ParticipantRowItem[] = []
    list = participantList.filter((e: any) => !selectedId.includes(e.id))
    return list
  }, [participantList])

  const blockClass = 'check-in-members-dialog-body'

  const maxChar = 8
  const memberNumber = memberId.substring(0, 8).split('')
  const spaces = Array(maxChar - memberNumber.length).fill('')

  const [dataState, mutate] = useMutation({
    method: 'POST',
    url: `/check-in`,
    isBase: false,
  })

  const [memberState, onGetMember] = useMutation({
    method: 'POST',
    url: `/participant/member`,
    isBase: false,
  })

  const [checkInState, onGetCheckIn] = useMutation({
    method: 'POST',
    url: `/check-in/participant`,
    isBase: false,
  })

  const [, onCheckIn] = useMutation({
    method: 'POST',
    url: `/check-in`,
    isBase: false,
  })

  const handleCheckout = async (participant: ParticipantRowItem, data: CheckOutFieldsType) => {
    mutate({
      data: {
        memberId,
        type: checkType,
        time: new Date().toISOString(),
        start: dayjs()
          .startOf('day')
          .toISOString(),
        end: dayjs()
          .endOf('day')
          .toISOString(),
        pick_up_name: data.pick_up_name,
        pick_up_contact_number: data.pick_up_contact_number,
        pick_up_relationship: data.pick_up_relationship,
      },
      onSuccess() {
        dispatch(hideDialog())
        dispatch(
          showSuccess({
            hideDialog: true,
            autohideTimeout: 2000,
            message: ``,
            subText: `${participant.first_name} \nsuccessfully checked out`,
          }),
        )
        dispatch(setRefreshPage(PAGENAME))
      },
    })
  }

  const handleShowCheckInDialog = useCallback(
    (participant: ParticipantRowItem, pass: boolean = false, autohideTimeout = 0) => {
      dispatch(
        showDialog({
          component: CheckInDetailsDialog,
          props: {
            validator,
            initialFields: {
              ...getEmptyCheckinFields(),
              isCheckedInAlready: pass,
            },
            hasCancel: true,
            continueLabel: pass ? '' : 'Check-in',
            cancelLabel: pass ? 'Done' : 'Cancel',
            participant: pick(participant, participatFields),
            autohideTimeout: autohideTimeout,
            onValid: () => {
              handleCheckin(participant)
            },
          },
        }),
      )
    },
    [handleCheckin],
  )

  const handleShowCheckOutDialog = useCallback(
    (participant: ParticipantRowItem) => {
      dispatch(
        showDialog({
          component: CheckOutDetailsDialog,
          props: {
            checkOutValidator,
            initialFields: {
              ...getEmptyCheckoutFields(),
            },
            isProcessing: false,
            hasCancel:
              participant.pick_up_permission != 'Child may travel to and from the facility independently'
                ? true
                : false,
            continueLabel:
              participant.pick_up_permission != 'Child may travel to and from the facility independently'
                ? 'Check-out'
                : 'Done',
            cancelLabel: 'Cancel',
            participant: pick(participant, participatFields),
            autohideTimeout:
              participant.pick_up_permission != 'Child may travel to and from the facility independently' ? 0 : 4000,
            onValid: async (data: CheckOutFieldsType) => {
              await handleCheckout(participant, data)
            },
          },
        }),
      )
    },
    [handleCheckout],
  )

  useEffect(() => {
    if (auto && memberId) {
      if (checkType === 'in') {
        onGetMember({
          data: { member_id: memberId },
          onSuccess(participant: ParticipantRowItem) {
            if (!isKiosk) dispatch(hideDialog())
            if (!participant.is_flagged) {
              handleShowCheckInDialog(participant, true, 4000)
              mutate({
                data: {
                  memberId,
                  type: checkType,
                  time: new Date().toISOString(),
                  start: dayjs()
                    .startOf('day')
                    .toISOString(),
                  end: dayjs()
                    .endOf('day')
                    .toISOString(),
                },
                onSuccess() {
                  if (refetch) refetch?.()

                  if (!refetch) dispatch(setRefreshPage(PAGENAME))

                  // onGetMember({
                  //   data: { member_id: memberId },
                  //   onSuccess(participant: ParticipantRowItem) {
                  //     if (!isKiosk) dispatch(hideDialog())
                  //     handleShowCheckInDialog(participant, true)
                  //   },
                  // })
                },
                onError(error: Error) {
                  if (
                    error?.message?.includes('did not pass') ||
                    error?.message?.includes('has not completed') ||
                    error?.message?.includes('has not verified')
                  ) {
                    // onGetMember({
                    //   data: { member_id: memberId },
                    //   onSuccess(participant: ParticipantRowItem) {
                    //     if (!isKiosk) dispatch(hideDialog())
                    //     handleShowCheckInDialog(participant)
                    //   },
                    // })
                  }
                },
              })
            } else {
              handleShowCheckInDialog(participant, isKiosk, 4000)
            }
          },
        })
      } else {
        onGetCheckIn({
          data: {
            memberId,
            type: checkType,
            start: dayjs()
              .startOf('day')
              .toISOString(),
            end: dayjs()
              .endOf('day')
              .toISOString(),
          },
          onSuccess() {
            onGetMember({
              data: { member_id: memberId },
              onSuccess(participant: ParticipantRowItem) {
                if (!isKiosk) dispatch(hideDialog())

                if (participant.pick_up_permission != 'Child may travel to and from the facility independently') {
                  handleShowCheckOutDialog(participant)
                } else {
                  mutate({
                    data: {
                      memberId,
                      type: checkType,
                      time: new Date().toISOString(),
                      start: dayjs()
                        .startOf('day')
                        .toISOString(),
                      end: dayjs()
                        .endOf('day')
                        .toISOString(),
                      pick_up_name: '',
                      pick_up_contact_number: '',
                      pick_up_relationship: '',
                    },
                    onSuccess() {
                      if (refetch) refetch?.()

                      if (!refetch) dispatch(setRefreshPage(PAGENAME))
                      dispatch(
                        showSuccess({
                          hideDialog: true,
                          autohideTimeout: 4000,
                          message: ``,
                          subText: `${participant.first_name} \nsuccessfully checked out`,
                        }),
                      )
                    },
                  })
                }
              },
            })
          },
        })
      }
    }
  }, [memberId, auto])

  const handleOnScan = useCallback(
    (barcode: string) => {
      if (auto) setMemberId(barcode?.substring(0, 8).trim() || '')
      else {
        onSearch(barcode)
      }
    },
    [auto, onSearch, setMemberId],
  )

  useEffect(() => {
    const OPTIONS = {
      onScan: handleOnScan,
      reactToKeyDown: true,
      reactToPaste: true,
      minLength: 5,
    }

    try {
      auto && onScan.attachTo(document, OPTIONS)
    } catch (err) {
      auto && onScan.setOptions(document, OPTIONS)
    }

    return () => auto && onScan.detachFrom(document)
  }, [auto])

  const CheckInElement = (
    <Content className={tabContentClassName || ''}>
      <Title className="title">
        Check {`${checkType === 'in' ? 'In Users to' : 'Out Users on'}`} {organization_information.name}
      </Title>
      <SelectAutocomplete
        isLoading={loading}
        value=""
        id="search"
        options={filteredList.map((e: any) => ({
          ...e,
          label: [e.first_name, e.last_name, e.member_id].join(' '),
          value: e.id,
        }))}
        components={{
          Option: ListOptions,
        }}
        onChange={(value: string) => {
          const selected = filteredList.find((e: any) => e.id === value)
          selected && onAdd(selected)
        }}
        onScrollBottom={onScrollBottom}
        onInputChange={onSearch}
        error=""
        label="Add Users"
        placeholder="Look up users by name or Member ID"
        className="check-in-member-menu"
      />
      <SelectedParticipantList list={fields.selected} onRemove={onRemove} checkType={checkType} />
    </Content>
  )

  const AutoElement = (
    <Box>
      <Content className={tabContentClassName || ''}>
        <Title className="title">
          Auto Check-{checkType === 'in' ? 'In' : 'Out'} Users to {organization_information.name}
        </Title>
        <MemberScanContainer>
          <label>Member ID</label>
          <MemberIdContainer>
            {[...memberNumber, ...spaces].map((each, number) => (
              <MemberIdNumber key={number}>{each}</MemberIdNumber>
            ))}
          </MemberIdContainer>
        </MemberScanContainer>

        {auto && memberId && dataState?.data && (
          <div className={`${blockClass}-self-checkin-container`}>
            {(dataState?.data || [])?.map((each: any, index: number) => (
              <div className={`${blockClass}-self-checkin-participant check-in-member-option`} key={index}>
                <ParticipantData data={each} />
                <div className={`${blockClass}-self-checkin-time`}>
                  <label>Time {`${checkType === 'in' ? 'In' : 'Out'}`}</label>
                  <span className="check-in-member__text">
                    {day(each[`${checkType === 'in' ? 'check_in_time' : 'check_out_time'}`]).format('hh:mm a')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {auto && memberId && dataState?.error && !dataState?.error?.message?.includes('did not pass') && (
          <TextInformation>{dataState?.error?.message}. Please try to rescan the barcode</TextInformation>
        )}

        {auto && memberId && memberState?.error && !memberState?.error?.message?.includes('did not pass') && (
          <TextInformation>{memberState?.error?.message}. Please try to rescan the barcode</TextInformation>
        )}

        {auto && memberId && checkInState?.data && (
          <div className={`${blockClass}-self-checkin-container`}>
            {(checkInState?.data || [])?.map((each: any, index: number) => (
              <div className={`${blockClass}-self-checkin-participant check-in-member-option`} key={index}>
                <ParticipantData data={each} />
                <div className={`${blockClass}-self-checkin-time`}>
                  <label>Time {`${checkType === 'in' ? 'In' : 'Out'}`}</label>
                  <span className="check-in-member__text">
                    {day(each[`${checkType === 'in' ? 'check_in_time' : 'check_out_time'}`]).format('hh:mm a')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {auto && memberId && checkInState?.error && !checkInState?.error?.message?.includes('did not pass') && (
          <TextInformation>{checkInState?.error?.message}. Please try to rescan the barcode</TextInformation>
        )}

        {auto && !memberId && <TextInformation>Please scan the barcode for the 8 digit Member ID</TextInformation>}
      </Content>
      <ActionsContainer>
        <Button variant="outline-primary" size="large" width="140px" mr="30px" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="large" width="140px" onClick={onCancel}>
          Done
        </Button>
      </ActionsContainer>
    </Box>
  )

  const tabs = [
    {
      element: CheckInElement,
      key: 'check-in',
      label: `Check-${`${checkType === 'in' ? 'In' : 'Out'}`}`,
      index: true,
    },
    {
      element: AutoElement,
      key: 'auto-check-in',
      label: `Auto Check-${`${checkType === 'in' ? 'In' : 'Out'}`}`,
    },
  ]

  return (
    <CheckInOutContainer>
      <Tabs
        tabs={tabs}
        tabId="check-in-sidebar"
        defaultTab={isKiosk ? 'auto-check-in' : 'check-in'}
        onChange={(str: any) => {
          if (str === 'check-in') {
            setAuto(false)
          } else {
            setAuto(true)
          }
        }}
      />
    </CheckInOutContainer>
  )

  function ListOptions(optionProps: Option) {
    const { data } = optionProps
    return (
      <CheckInMemberOption onClick={() => onAdd(data)}>
        <ParticipantData data={data} />
        <img src={IconAddParticipant} alt="Add Participant" />
      </CheckInMemberOption>
    )
  }

  function onAdd(participant: ParticipantRowItem) {
    formHandlers.onElementChange(fields.selected.concat(participant), 'selected')
  }

  function onRemove(id: string) {
    formHandlers.onElementChange(
      fields.selected.filter(e => e.id !== id),
      'selected',
    )
  }

  function handleCheckin(participant: ParticipantRowItem) {
    onCheckIn({
      data: {
        memberId: participant?.member_id,
        type: 'in',
        time: new Date().toISOString(),
        start: dayjs()
          .startOf('day')
          .toISOString(),
        end: dayjs()
          .endOf('day')
          .toISOString(),
      },
      onSuccess() {
        dispatch(hideDialog())
        dispatch(setRefreshPage(PAGENAME))
      },
      onError() {
        dispatch(dialogProcessing(false))
      },
    })
  }
}

type SelectedParticipantListProps = {
  checkType: string
  list: ParticipantRowItem[]
  onRemove(id: string): void
}
function SelectedParticipantList(props: SelectedParticipantListProps) {
  const { list, onRemove, checkType } = props
  return (
    <>
      <ListTitle className="list-title">List of Users</ListTitle>
      <UsersList className="users-list">
        {list.length > 0 &&
          list.map((data, index) => (
            <UsersListItem key={index}>
              <Box className="user-data">
                {data?.hasfilled && <SVGScreenedIcon />}
                {!data?.hasfilled && <span className="empty-icon" />}
                <ParticipantData data={data} />
              </Box>
              <Button onClick={() => onRemove(data.id)}>
                <img src={IconRemove} alt="Remove User" />
              </Button>
            </UsersListItem>
          ))}
        {!list.length && (
          <EmptyListLabel className="empty-list-table">
            No users selected yet, begin by adding them to the list.
          </EmptyListLabel>
        )}
      </UsersList>
    </>
  )
}
type ParticipantDataProps = {
  data: ParticipantRowItem & {
    role_name?: string
    role_display_name?: string
  }
}
export function ParticipantData(props: ParticipantDataProps) {
  const { data } = props
  const { photo_url: photo, image_link = '', member_id: memberId, role_name, role_display_name } = data
  const name = [data.first_name, data.last_name].join(' ')
  const blockClass = 'check-in-member'
  return (
    <CheckInMember>
      <div className={`${blockClass}__image-container`}>
        <ProtectedImage
          style={{ height: '100%', width: '100%' }}
          src={image_link ? '' : photo || ''}
          defaultSrc={image_link ? image_link : UserPlaceholder}
          alt=""
          className={`${blockClass}__photo`}
        />
      </div>
      <CheckInMemberDetails>
        <CheckInMemberText $bold={true} mb="5px">
          {name}
        </CheckInMemberText>
        {memberId && <CheckInMemberText>Member ID: {memberId}</CheckInMemberText>}
        {!memberId && (role_display_name || role_name) && (
          <CheckInMemberText>User Group: {role_display_name || role_name}</CheckInMemberText>
        )}
      </CheckInMemberDetails>
    </CheckInMember>
  )
}

const CheckInParticipantDialog = flowRight(withDialog())(CheckInParticipant)

CheckInParticipantDialog.defaultProps = {
  continueIsDisabled(form: CheckInForm) {
    return form.selected.length === 0
  },
  minimize: true,
  dialogClassName: 'check-in-members-dialog',
  footerClassName: 'dialog__footer--less-padding',
}

const EmptySpace = styled(Box)`
  height: 19px;
`
const CheckInOutContainer = styled(Box)`
  padding: 30px 0 0;
`

const Title = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: bold;
  font-size: 28px;
  margin-bottom: 50px;
`

const Content = styled(Box)`
  height: calc(100vh - 216px);
  padding: 20px 30px 0;

  @media screen and (max-width: 1025px) {
    height: calc(100vh - 190px);
  }

  &.kiosk-tab-content {
    max-width: 1000px;
    margin: auto;
    padding: 30px 15px 0;

    .title {
      margin-bottom: 30px;
    }

    .list-title {
      margin-top: 30px;
      margin-bottom: 15px;
    }

    .users-list {
      height: calc(100vh - 500px);
      .empty-list-table {
        margin-top: 10px;
      }
    }
  }
`

const EmptyListLabel = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 16px;
  margin-top: 30px;
`

const ListTitle = styled(Box)`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: 60px;
  margin-bottom: 30px;
`

export const CheckInMemberOption = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 8px 8px;
  margin: 8px;
  border-radius: ${({ theme }) => theme.borderRadius};
  border: 1px solid #c7c7c7;

  &:hover {
    background: ${({ theme }) => theme.colors.tableAlternate};
    cursor: pointer;
  }
`

const UsersList = styled(Box)`
  overflow: auto;
  height: calc(100vh - 544px);

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f5f5f5;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.label};
    border-radius: 50px;
  }
`

const UsersListItem = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 33px 18px 8px;
  margin-right: 17px;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  .user-data {
    display: flex;
    align-items: center;

    & > *:first-child {
      margin-right: 30px;
    }
  }

  .empty-icon {
    width: 24px;
  }
`

const CheckInMember = styled(Box)`
  display: flex;

  &__image-container {
    @include themify($themes) {
      border: themed('participant-pic-border');
    }
    height: 56px;
    width: 56px;
    border-radius: 4px;
    overflow: hidden;
  }

  &__photo {
    max-width: 100%;
  }

  &__details {
    display: flex;
    flex-direction: column;
    margin-left: 16px;
  }
`

const CheckInMemberDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 10px;
`

const CheckInMemberText = styled(Box)<{ $bold?: boolean }>`
  font-size: 16px;
  font-weight: ${({ $bold }) => ($bold ? '500' : 'normal')};
  line-height: 19px;
  text-transform: capitalize;
`

const MemberScanContainer = styled(Box)`
  text-align: center;

  label {
    font-size: 28px;
    color: ${({ theme }) => theme.colors.secondary};
    line-height: 33px;
    font-weight: bold;
  }
`

const MemberIdContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
`

const MemberIdNumber = styled(Box)`
  height: 50px;
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 34px;
  font-weight: bold;
  ${({ theme }) => `
    color: ${theme.colors.secondary};
    border: 1px solid ${theme.colors.inputBorder};
    border-radius: ${theme.borderRadius};
  `}
`

const TextInformation = styled(Box)`
  font-size: 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: 30px;
`

const ActionsContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  padding-bottom: 60px;
`

export default CheckInParticipantDialog
